import React, { useMemo, useState, memo } from "react";
import styles from "./styles.module.css";
import { FireIcon } from "@shared/ui/icon/Fire";
import { Modal } from "../Modal/Modal";
import {
  usePurchaseProductMutation,
  usePinProductMutation,
  useUnpinProductMutation,
} from "@entities/Shop/api/shopApiSlice";
import { useDispatch, useSelector } from "react-redux";
import { addToast } from "@features/toasts/toastSlice";
import { selectTradeLink } from "@entities/User/model/slice";
import { useLazyGetUserInfoQuery } from "@entities/User/api/UserInformationSlice";
import { setUserInfo } from "@entities/User/model/slice";

interface ProductCardProps {
  id: string;
  name: string;
  price: number;
  image: string;
  isPremium?: boolean;
  userMoney: number;
  userPremium?: boolean;
  isTypeCardPined?: boolean;
  userRole: boolean;
  isCardPined?: boolean;
  isAvable?: boolean;
  rarity?: string;
  onPurchase?: () => void;
  position?: number;
}

// Updated to work with string rarity values
const getCardTypeByRarity = (rarityValue: string | undefined) => {
  if (!rarityValue) return "1";

  switch (rarityValue) {
    case "Factory New":
      return "1";
    case "Minimal Wear":
      return "2"; // Gold card
    case "Field-Tested":
      return "3"; // Silver card
    case "Well-Worn":
      return "4"; // Silver card
    case "Battle-Scarred":
      return "5"; // Black card
    default:
      return "1"; // Default to Factory New
  }
};

// Обновляем функцию валидации для пина
const validatePin = (
  userPremium: boolean | undefined,
  isAvable: boolean | undefined
): string | null => {
  if (!isAvable) return "This item is not available for pinning";
  if (!userPremium) return "Only VIP users can pin items";
  return null;
};

export const ProductCard: React.FC<ProductCardProps> = memo(
  ({
    id,
    name,
    price,
    image,
    isPremium,
    rarity,
    userMoney,
    userPremium,
    isAvable,
    isTypeCardPined,
    userRole,
    isCardPined: initialIsCardPined,
    onPurchase,
    position,
  }) => {
    const [isFalling, setIsFalling] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const dispatch = useDispatch();
    const [purchaseProduct] = usePurchaseProductMutation();
    const [pinProduct] = usePinProductMutation();
    const [unpinProduct] = useUnpinProductMutation();
    const [isPinProcessing, setIsPinProcessing] = useState(false);
    const [localIsCardPined, setLocalIsCardPined] =
      useState(initialIsCardPined);
    const tradeLink = useSelector(selectTradeLink);
    const [getUserInfo] = useLazyGetUserInfoQuery();

    const randomRotate = useMemo(() => (Math.random() * 6 - 3).toFixed(2), []);
    const nameParts = useMemo(() => {
      const parts = name.split("|");
      return {
        collection: parts[0]?.trim() || "",
        skinName: parts[1]?.trim() || name,
      };
    }, [name]);

    const rarityText = rarity || "Factory New";

    const handleToggle = () => {
      if (!isAvable) return;
      if (userRole) return;
      setIsOpen((prev) => !prev);
    };

    const validatePurchase = (): string | null => {
      if (!tradeLink)
        return "Please set your Steam Trade Link first. Trade link in profile.";
      if (userMoney < price)
        return "You don't have enough money to buy this item.";
      if (isPremium && !userPremium)
        return "This item is available only for VIP users.";
      return null;
    };

    const handlePurchase = async () => {
      if (isProcessing) return;

      const validationError = validatePurchase();
      if (validationError) {
        dispatch(
          addToast({
            title: "Purchase Error",
            description: validationError,
            type: "error",
            duration: 2000,
          })
        );
        return;
      }

      try {
        setIsProcessing(true);
        const result = await purchaseProduct({ productId: id }).unwrap();
        
        // Обновляем информацию о пользователе
        const userInfo = await getUserInfo().unwrap();
        dispatch(setUserInfo(userInfo));
        
        dispatch(
          addToast({
            title: "Purchase Successful",
            description: `Your trade code: ${result.tradeOfferId}`,
            type: "success",
            duration: 2000,
          })
        );
        setIsFalling(true);
        setTimeout(() => {
          onPurchase?.();
          setIsOpen(false);
          setIsFalling(false);
        }, 100);
      } catch (e) {
        console.error(e);
        dispatch(
          addToast({
            title: "Purchase Error",
            description:
              "An error occurred during the purchase. Please try again.",
            type: "error",
            duration: 2000,
          })
        );
      } finally {
        setIsProcessing(false);
      }
    };

    const handlePinToggle = async (e: React.MouseEvent) => {
      e.stopPropagation();
      if (isPinProcessing) return;

      const validationError = validatePin(userPremium, isAvable);
      if (validationError) {
        dispatch(
          addToast({
            title: "Pin Error",
            description: validationError,
            type: "error",
            duration: 2000,
          })
        );
        return;
      }

      try {
        setIsPinProcessing(true);
        if (localIsCardPined) {
          await unpinProduct().unwrap();
          setLocalIsCardPined(false);
          dispatch(
            addToast({
              title: "Success",
              description: "Item unpinned successfully",
              type: "success",
              duration: 2000,
            })
          );
        } else {
          await pinProduct({ productId: id, position: position || 0 }).unwrap();
          setLocalIsCardPined(true);
          dispatch(
            addToast({
              title: "Success",
              description: "Item pinned successfully",
              type: "success",
              duration: 2000,
            })
          );
        }
        onPurchase?.();
      } catch (error: any) {
        let errorMessage = "Failed to update pin status";
        if (error?.data?.message === "Another item is already pinned") {
          errorMessage = "You can only pin one item at a time";
        }
        dispatch(
          addToast({
            title: "Error",
            description: errorMessage,
            type: "error",
            duration: 2000,
          })
        );
      } finally {
        setIsPinProcessing(false);
      }
    };

    const cardStyle = useMemo(
      () => ({
        background: `url(/Card/Card${getCardTypeByRarity(rarity)}.png)`,
        backgroundSize: "100% 100%",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        ["--random-rotate" as string]: `${randomRotate}deg`,
        cursor: "pointer",
      }),
      [randomRotate, rarity]
    );

    return (
      <>
        <div
          className={`${styles.card} ${isFalling ? styles.falling : ""}`}
          style={cardStyle}
          onClick={handleToggle}
        >
          {/* Иконка отображается, если isPined === true */}
          {isTypeCardPined && (
            <div
              className={`${styles.pinedIcon} ${isPinProcessing ? styles.processing : ""} ${!userPremium ? styles.disabled : ""}`}
              onClick={handlePinToggle}
              title={
                !userPremium
                  ? "Only VIP users can pin items"
                  : localIsCardPined
                    ? "Unpin item"
                    : "Pin item"
              }
            >
              {localIsCardPined ? (
                <svg
                  width="16"
                  height="20"
                  viewBox="0 0 16 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M5 5C5 4.20435 5.31607 3.44129 5.87868 2.87868C6.44129 2.31607 7.20435 2 8 2C8.79565 2 9.55871 2.31607 10.1213 2.87868C10.6839 3.44129 11 4.20435 11 5V7H5V5ZM3 7V5C3 3.67392 3.52678 2.40215 4.46447 1.46447C5.40215 0.526784 6.67392 0 8 0C9.32608 0 10.5979 0.526784 11.5355 1.46447C12.4732 2.40215 13 3.67392 13 5V7H15C15.2652 7 15.5196 7.10536 15.7071 7.29289C15.8946 7.48043 16 7.73478 16 8V17C16 17.7956 15.6839 18.5587 15.1213 19.1213C14.5587 19.6839 13.7956 20 13 20H3C2.20435 20 1.44129 19.6839 0.87868 19.1213C0.316071 18.5587 0 17.7956 0 17V8C0 7.73478 0.105357 7.48043 0.292893 7.29289C0.48043 7.10536 0.734784 7 1 7H3ZM9 13.5C9 13.1022 9.15804 12.7206 9.43934 12.4393C9.72064 12.158 10.1022 12 10.5 12H10.51C10.9078 12 11.2894 12.158 11.5707 12.4393C11.852 12.7206 12.01 13.1022 12.01 13.5V13.51C12.01 13.9078 11.852 14.2894 11.5707 14.5707C11.2894 14.852 10.9078 15.01 10.51 15.01H10.5C10.1022 15.01 9.72064 14.852 9.43934 14.5707C9.15804 14.2894 9 13.9078 9 13.51V13.5Z"
                    fill="white"
                  />
                </svg>
              ) : (
                <svg
                  width="16"
                  height="20"
                  viewBox="0 0 16 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g opacity="0.48">
                    <path
                      d="M4 8V5C4 3.93913 4.42143 2.92172 5.17157 2.17157C5.92172 1.42143 6.93913 1 8 1C9.06087 1 10.0783 1.42143 10.8284 2.17157C11.5786 2.92172 12 3.93913 12 5V8"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <path
                      d="M1 8H15V17C15 17.5304 14.7893 18.0391 14.4142 18.4142C14.0391 18.7893 13.5304 19 13 19H3C2.46957 19 1.96086 18.7893 1.58579 18.4142C1.21071 18.0391 1 17.5304 1 17V8Z"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M10.5 13.5H10.51V13.51H10.5V13.5Z"
                      stroke="white"
                      strokeWidth="3.75"
                      strokeLinejoin="round"
                    />
                  </g>
                </svg>
              )}
              {isPinProcessing && (
                <circle
                  className={styles.pinSpinner}
                  cx="8"
                  cy="10"
                  r="6"
                  stroke="white"
                  strokeWidth="2"
                  fill="none"
                />
              )}
            </div>
          )}
          {isPremium && (
            <img
              src="/icons/VIP.svg"
              alt="VIP ONLY"
              className={styles.premiumBadge}
            />
          )}
          {userRole && (
            <img
              src="/icons/pleaseLogin.svg"
              alt="PleaseLoing"
              className={styles.premiumBadge}
            />
          )}
          {!isAvable && (
            <img
              src="/icons/TradeBan.svg"
              alt="Is not available"
              className={styles.availableBadge}
            />
          )}
          <div className={styles.cardContent}>
            {/* Header */}
            <div className={styles.header}>
              <h2 className={styles.title} style={{ color: "#000000" }}>
                {nameParts.skinName}
              </h2>
              <p className={styles.subtitle} style={{ color: "#000000" }}>
                {rarityText}
              </p>
            </div>

            {/* Image */}
            <div className={styles.imageWrapper}>
              <div className={styles.imageContainer}>
                <img
                  src={`https://community.cloudflare.steamstatic.com/economy/image/${image}`}
                  alt={name}
                  className={styles.image}
                />
              </div>
            </div>

            {/* Footer */}
            <div className={styles.footer}>
              <div className={styles.barcode}>
                <img src="/icons/barcodeBlack.svg" alt="barcode" />
              </div>
              <div className={styles.priceContainer}>
                <span className={styles.price} style={{ color: "#000000" }}>
                  {price}
                </span>
                <FireIcon active="black" width={20} height={20} />
              </div>
            </div>
          </div>
        </div>
        <Modal isOpen={isOpen} onClose={handleToggle} mountPoint="shop-frame">
          <div className={styles.modalContent}>
            <button className={styles.closeModalButton} onClick={handleToggle}>
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M14.1454 15.6818C14.5697 16.1061 15.2576 16.1061 15.6818 15.6818C16.1061 15.2576 16.1061 14.5697 15.6818 14.1454L9.53636 8L15.6818 1.85455C16.1061 1.4303 16.1061 0.742446 15.6818 0.318191C15.2576 -0.106064 14.5697 -0.106063 14.1454 0.318191L8 6.46364L1.85455 0.318191C1.4303 -0.106064 0.742446 -0.106064 0.318191 0.318191C-0.106064 0.742446 -0.106063 1.4303 0.318191 1.85455L6.46364 8L0.318191 14.1454C-0.106064 14.5697 -0.106064 15.2576 0.318191 15.6818C0.742446 16.1061 1.4303 16.1061 1.85455 15.6818L8 9.53636L14.1454 15.6818Z"
                  fill="white"
                />
              </svg>
              CLOSE
            </button>

            <div className={styles.modalCard}>
              <div className={styles.card} style={cardStyle}>
                <div className={styles.cardContent}>
                  <div className={styles.header}>
                    <h2 className={styles.title}>{nameParts.skinName}</h2>
                    <p className={styles.subtitle}>{rarityText}</p>
                  </div>
                  <div className={styles.imageWrapper}>
                    <div className={styles.imageContainer}>
                      <img
                        src={`https://community.cloudflare.steamstatic.com/economy/image/${image}`}
                        alt={name}
                        className={styles.image}
                      />
                    </div>
                  </div>
                  <div className={styles.footer}>
                    <div className={styles.barcode}>
                      <img src="/icons/barcodeBlack.svg" alt="barcode" />
                    </div>
                    <div className={styles.priceContainer}>
                      <span className={styles.price}>{price}</span>
                      <FireIcon active="black" width={20} height={20} />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.modalPurchase}>
              <div className={styles.modalTitle}>
                <p className={styles.modalH1} style={{ color: "#FFFFFF" }}>
                  STEAM SHOP
                </p>
                <p className={styles.modalP} style={{ color: "#FFFFFF" }}>
                  INSTRUCTION TO USE
                </p>
              </div>
              <div className={styles.instruction}>
                {[
                  {
                    title: "STEP 1",
                    text: "Ты уверен, что хочешь именно этот скин? Ты столько потел — не промахнись с выбором.",
                  },
                  {
                    title: "STEP 2",
                    text: "Убедись, что в профиле указан актуальный trade link. Если забыл — сможешь добавить после нажатия «Purchase».",
                  },
                  {
                    title: "STEP 3",
                    text: "Жми «Purchase» — трейд прилетит сразу в Steam. Забирай и кайфуй. Ты это заслужил.",
                  },
                ].map((step, index) => (
                  <div key={index} className={styles.step}>
                    <h3
                      className={styles.stepTitle}
                      style={{ color: "#FFFFFF" }}
                    >
                      {step.title}
                    </h3>
                    <p className={styles.stepText} style={{ color: "#FFFFFF" }}>
                      {step.text}
                    </p>
                  </div>
                ))}
              </div>
              <div className={styles.modalPurchaseB}>
                <p style={{ color: "#FFFFFF" }}>
                  Pressing the button, I accept rules and regulations of{" "}
                  <span className={styles.eula} style={{ color: "#FFFFFF" }}>
                    EULA
                  </span>
                </p>
                <button
                  className={`${styles.modalPurchaseButton} ${isProcessing ? styles.processing : ""}`}
                  onClick={handlePurchase}
                  disabled={isProcessing}
                >
                  {isProcessing ? (
                    <div className={styles.loadingWrapper}>
                      <div className={styles.spinner}></div>
                      <span>PROCESSING...</span>
                    </div>
                  ) : (
                    <>
                      PURCHASE{" "}
                      <span>
                        {price}{" "}
                        <FireIcon active="black" width={22} height={28} />
                      </span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </Modal>
      </>
    );
  }
);

ProductCard.displayName = "ProductCard";
